import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { UserCompanyInformationCollectionDto } from '@treasury/api/channel';
import { ConfigurationService } from '@treasury/core/config';
import { NavigationService } from '@treasury/core/navigation';
import { AccountService, ChannelAuthenticationService } from '@treasury/domain/channel/services';
import { StatusCode } from '@treasury/domain/channel/services/login';
import { AuthenticationService } from '@treasury/domain/services/authentication';
import { TmBaseComponent } from '@treasury/presentation';
import { SessionStorageService } from '@treasury/utils';
import { css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { UserOptionViewModel } from './components/user-selection';
import './login-container';

export const tagName = 'sso-callback-container';
@customElement(tagName)
export class SsoContainer extends TmBaseComponent {
    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private authService: AuthenticationService;

    @InjectProperty()
    declare private channelAuthService: ChannelAuthenticationService;

    @InjectProperty()
    declare private accountService: AccountService;

    @InjectProperty()
    declare private readonly config: ConfigurationService;

    @InjectProperty()
    declare private readonly sessionStorageService: SessionStorageService;

    @state()
    private errorMessage?: string;

    @state()
    private loading = true;

    @state()
    private statusCode?: string;

    @state()
    private userOptions: Array<UserOptionViewModel> = [];

    protected async firstUpdated() {
        const urlParams = new URLSearchParams(window.location.search);
        const url = new URL(window.location.href);
        const isBffLogin = url.pathname.includes('/bff');
        const code = urlParams.get('code');
        const state = urlParams.get('state');

        try {
            this.loading = true;
            let userCompanyInformation: UserCompanyInformationCollectionDto | undefined;
            let statusCode: string | undefined;
            let message: string | undefined;

            if (isBffLogin) {
                userCompanyInformation = await this.authService.getCurrentUisUserCompanies();
                statusCode = StatusCode.Allow;
            } else {
                const result = await this.authService.authenticationSsoViaUis(
                    code ?? '',
                    state ?? ''
                );
                ({ statusCode, message, userCompanyInformation } = result);
            }
            // Go to user selection if more than one TM is linked to the UIS ID
            if (userCompanyInformation && userCompanyInformation.length > 1) {
                this.statusCode = StatusCode.UserSelection;
                this.userOptions = userCompanyInformation;
                return;
            }

            if (isBffLogin && userCompanyInformation && userCompanyInformation.length === 1) {
                // For BFF logins with a single linked TM user, we need to authenticate that user
                const userCompanyUniqueId = userCompanyInformation?.[0]?.userCompanyUniqueId;
                if (!userCompanyUniqueId) {
                    throw new Error('No linked TM User ID found for the authenticated UIS user.');
                }

                const response = await this.authService.authenticateSelectedCompanyUser(
                    userCompanyUniqueId,
                    true
                );
                message = response.message;
            }

            // If only one user is linked go ahead and log them in
            this.statusCode = await this.channelAuthService.startAuthWorkflow({
                statusCode,
                message,
                numberOfSecurityQuestions: 0,
                daysUntilPasswordExpires: 0,
            });
        } catch (ex) {
            this.errorMessage =
                ex instanceof Error
                    ? ex.message
                    : 'An error occurred while logging in. Please try again.';
        } finally {
            this.loading = false;
        }
    }

    protected render() {
        if (this.loading || this.statusCode === StatusCode.Loading) {
            return html`<blocking-loader></blocking-loader>`;
        }

        if (this.errorMessage) {
            return this.renderErrorMessage();
        }

        if (this.statusCode && this.statusCode !== StatusCode.Loading && !this.errorMessage) {
            return html`<login-container
                .statusCode=${this.statusCode}
                .userOptions=${this.userOptions}
            ></login-container>`;
        }

        return nothing;
    }

    private renderErrorMessage() {
        if (!this.errorMessage) {
            return nothing;
        }
        return html`
            <div class="error">
                <omega-icon icon="times-circle"></omega-icon>
                ${this.errorMessage}
            </div>
        `;
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: block;
                }

                @media screen and (min-width: 1024px) {
                    :host {
                        max-width: 50vw;
                    }
                }

                .error {
                    border: 1px solid #ee3a3b;
                    border-left: 4px solid #ee3a3b;
                    padding: 7px 15px;
                    margin: 15px;
                    display: flex;
                    align-items: center;
                }
                omega-icon {
                    margin-right: 16px;
                    color: #ee3a3b;
                    font-size: 24px;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: SsoContainer;
    }
}
